import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { api } from '@/api/client'
import type { AskAttachmentRef } from '@/api/chat'
import type { Subscription } from '@/api/notification-preferences'

/**
 * O robô atribuído a quem está logado.
 *
 * `owner_named_at` é o que decide se o chat abre — e não a string `name`, que
 * é `NOT NULL` desde a criação da tabela e por isso sempre tem algum valor (o
 * rótulo de inventário que o admin digitou ao cadastrar). Chat só depois que o
 * DONO nomeou.
 */
/**
 * O perfil de DISPLAY, que é o que seleciona o binário. "Robô I"/"Robô II" são
 * rótulos de tela: produto se renomeia, o perfil técnico não. `null` é "ainda
 * não sabemos qual placa é" — diferente de chutar uma.
 */
export type HardwareModel = 'oled_dual' | 'tft_st7735'

/** Só existe no Robô II. `null` é "o dono não escolheu", e o visor desenha o Inovare. */
export type Avatar = 'inovare' | 'r2d2' | 'c3po' | 'yoda' | 'peter'

export const MODELO_LABEL: Record<HardwareModel, string> = {
  oled_dual: 'Robô I',
  tft_st7735: 'Robô II',
}

export const AVATAR_LABEL: Record<Avatar, string> = {
  inovare: 'Inovare',
  r2d2: 'R2-D2',
  c3po: 'C-3PO',
  yoda: 'Yoda',
  peter: 'Peter',
}

/** A ordem em que a tela do dono os apresenta: o padrão da casa primeiro. */
export const AVATARES: Avatar[] = ['inovare', 'r2d2', 'c3po', 'yoda', 'peter']

/** Avatar só existe neste perfil — os personagens vivem no TFT. */
export const HARDWARE_COM_AVATAR: HardwareModel = 'tft_st7735'

/**
 * O veredito sobre a versão de firmware que o robô anunciou, montado pelo
 * backend (`consoleai.robo.releases.classificar`) — o front nunca reclassifica
 * o `firmware_version` cru, só exibe o que já veio decidido.
 */
/** `candidata`: versão do changelog acima da homologada (spec 2026-10-02, 4.3). */
export type EstadoDoFirmware = 'em_dia' | 'desatualizado' | 'desenvolvimento' | 'anterior' | 'desconhecido' | 'candidata'
export type ReleaseDeFirmware = {
  versao: string
  data: string
  mudancas: string[]
  /** Só em `/releases`. `null` = não homologada; `por_nome` `null` = homologada na implantação. */
  homologada?: { em: number; por_nome: string | null } | null
}
export type FirmwareVeredito = {
  estado: EstadoDoFirmware
  perfil: string | null
  nome: string | null
  versao: string | null
  ultima: string | null
  faltam: ReleaseDeFirmware[]
  modelo_divergente: boolean
}
export type ModeloDeRelease = { perfil: string; nome: string; releases: ReleaseDeFirmware[] }

/**
 * Veredito de estado e de credencial, decidido pelo backend (`robo/estado.py`,
 * TASK-11258). A tela formata; quem decide e' o servidor, no relogio dele.
 */
export type ChaveEstado =
  | 'nunca_conectou' | 'ativo' | 'atrasado' | 'sem_contato' | 'suspenso' | 'aposentado'
export type ChaveAviso = 'sem_credencial' | 'ok' | 'vence_em_breve' | 'vencida'

export type Robo = {
  public_id: string
  name: string
  /** `null` no robô da plataforma; número só no robô legado (a Ivy). */
  tenant_id: number | null
  tenant_name: string | null
  status: string
  last_seen_at: string | null
  estado: ChaveEstado
  owner_named_at: string | null
  hardware_model: HardwareModel | null
  avatar: Avatar | null
  /** O som do Robô II (spec 2026-10-08-robo-som). Os outros modelos guardam `true` e não tocam. */
  som: boolean
  /** Volume do Robô II em %, 10..100 de 10 em 10 (padrão 70). */
  volume: number
  /** A placa registrada; `null` enquanto a frota não gravou o firmware. */
  mac?: string | null
  /**
   * Por qual caminho o robô consultou o snapshot na última hora (revisão R2).
   * `antiga` = o visor segue o tenant do cadastro, não o escopo escolhido.
   * `null` = sem consulta na última hora, ou Redis fora. Só o GET devolve.
   */
  rota?: RotaDoRobo | null
  /** A reação do robô a este salvamento. Só as mutações devolvem. */
  aviso?: AvisoDoAjuste | null
  /** O que falta o dono fazer, na ordem do wizard. GET e POST de recebimento devolvem. */
  recebimento?: Recebimento
}

export type Pendencia = 'recebimento' | 'nome' | 'escopo'

/**
 * `estado` nulo e' o robo atribuido antes do recebimento existir: conta como
 * confirmado (ninguem confirmou, e nao se inventa). A regra mora no backend
 * (`robo/recebimento.py`); a tela so' le `pendencias`.
 */
export type Recebimento = {
  estado: 'pendente' | 'recebido' | 'nao_recebido' | null
  pendencias: Pendencia[]
  atribuido_por: string | null
  atribuido_em: string | null
}

export type RotaDoRobo = 'antiga' | 'nova'

/**
 * Uma consulta substitui a dupla `useUserTenants().find('tecpag-prd')` +
 * `can('zabbix','read')` que a tela usava para decidir tudo.
 *
 * A troca não é cosmética: aquele par respondia "o usuário pode ler alertas do
 * TecPag-PRD?", que é uma pergunta diferente de "qual robô é dele?". Com um
 * robô por pessoa, quem tem acesso ao tenant e nenhum robô via um contato que
 * não existia, e quem tem robô em outro tenant não via nenhum.
 *
 * 404 é a resposta NORMAL de quem não tem robô — não é erro a reportar.
 */
export function useMeuRobo(opcoes: { refetchInterval?: number | false; enabled?: boolean } = {}) {
  const consulta = useQuery({
    enabled: opcoes.enabled ?? true,
    queryKey: ['meu-robo'],
    queryFn: async (): Promise<Robo | null> => {
      try {
        return (await api.get<Robo>('/v1/me/robo')).data
      } catch (erro) {
        if (isAxiosError(erro) && erro.response?.status === 404) return null
        throw erro
      }
    },
    staleTime: 5 * 60 * 1000,
    // Mesma chave para todos: quem pede o intervalo (o passo Rede, com o robô
    // sem sinal) faz a consulta de todos andar, e o wizard percebe a volta.
    refetchInterval: opcoes.refetchInterval,
    retry: (contagem, erro) =>
      contagem < 1 && !(isAxiosError(erro) && [401, 403, 404].includes(erro.response?.status ?? 0)),
  })
  return {
    robo: consulta.data ?? null,
    carregando: consulta.isLoading,
    erro: consulta.isError,
  }
}

/** O dono batiza (ou rebatiza) o robô dele. */
export async function nomearMeuRobo(name: string): Promise<Robo> {
  return (await api.patch<Robo>('/v1/me/robo', { name })).data
}

export const BASE_MEU_ROBO = '/v1/me/robo'

/**
 * "Recebi" (ou "não recebi") o robô. `recebido` não volta: 409
 * `recebimento_ja_confirmado`. Quem chama invalida `['meu-robo']`.
 */
export async function confirmarRecebimento(recebi: boolean): Promise<Robo> {
  return (await api.post<Robo>(`${BASE_MEU_ROBO}/recebimento`, { recebi })).data
}

/**
 * O dono escolhe a cara do robo dele. Mesma rota do nome, de proposito: e' o
 * mesmo recurso e a mesma porta de posse.
 *
 * Quem NAO tem Robo II recebe 422 `avatar_indisponivel_neste_hardware` — a tela
 * ja nao oferece a escolha, mas o backend continua sendo quem decide.
 */
export async function escolherAvatar(avatar: Avatar): Promise<Robo> {
  return (await api.patch<Robo>(BASE_MEU_ROBO, { avatar })).data
}

/** Liga ou desliga o som do Robô II (spec 2026-10-08-robo-som §4.2). Robô I dá 422. */
export async function escolherSom(som: boolean): Promise<Robo> {
  return (await api.patch<Robo>(BASE_MEU_ROBO, { som })).data
}

/** Ajusta o volume do Robô II (10..100, de 10 em 10). Robô I dá 422. */
export async function escolherVolume(volume: number): Promise<Robo> {
  return (await api.patch<Robo>(BASE_MEU_ROBO, { volume })).data
}

/**
 * O que o robô mostra (spec 2026-09-24, 4.3; TASK-11447). O dono escolhe UMA
 * organização e "todos" os tenants dela ou uma lista. O `efetivo` é o que
 * vale agora, cortado pelo acesso dele no backend — gravar não concede nada.
 */
export type ModoDeEscopo = 'todos' | 'lista'
export type EscopoGravado = { organization_id: number; modo: ModoDeEscopo; tenant_ids: number[] }
export type MotivoDeEscopo = 'sem_dono' | 'sem_escopo' | 'sem_acesso' | null
export type EscopoDoRobo = {
  gravado: EscopoGravado | null
  efetivo: number[]
  sem_acesso: number[]
  motivo: MotivoDeEscopo
  organizacao_nome?: string | null
  /** Nome dos tenants que o dono marcou, por id — o selo "Sem acesso" precisa dele. */
  nomes?: Record<string, string>
  /**
   * Tenants do escopo que o robô não recebeu no último painel (spec 2026-10-08 §4.4).
   * `null` = não se sabe (robô parado ou sem painel); `[]` = nada faltando.
   */
  sem_dados?: number[] | null
  /** A reação do robô ao salvamento. Só o PUT devolve. */
  aviso?: AvisoDoAjuste | null
}
export type OpcaoDeOrganizacao = {
  id: number
  nome: string
  tenants: { id: number; nome: string; monitorado?: boolean }[]
}

export const CHAVE_ESCOPO = ['meu-robo', 'escopo'] as const
export const CHAVE_OPCOES_ESCOPO = ['meu-robo', 'escopo', 'opcoes'] as const

export function useEscopoDoMeuRobo() {
  return useQuery({
    queryKey: CHAVE_ESCOPO,
    queryFn: async () => (await api.get<EscopoDoRobo>(`${BASE_MEU_ROBO}/escopo`)).data,
    retry: false,
  })
}

export function useOpcoesDeEscopo() {
  return useQuery({
    queryKey: CHAVE_OPCOES_ESCOPO,
    queryFn: async () =>
      (await api.get<{ organizacoes: OpcaoDeOrganizacao[] }>(`${BASE_MEU_ROBO}/escopo/opcoes`))
        .data.organizacoes,
    retry: false,
  })
}

/**
 * A faixa "nunca" do robô (spec 2026-09-30-robo-agente-pessoal §4.1): ações que ele
 * nunca propõe. Só tira — não concede nada a ninguém. `tenant_id` nulo vale em todo
 * ambiente; `alvo` é o `action_type` do cartão ou `host_order:<verbo>`.
 */
export type Restricao = { alvo: string; tenant_id: number | null }
export type OpcaoDeRestricao = { alvo: string; rotulo: string }
export const CHAVE_RESTRICOES = ['meu-robo', 'restricoes'] as const

export function useRestricoesDoRobo() {
  return useQuery({
    queryKey: CHAVE_RESTRICOES,
    queryFn: async () =>
      (await api.get<{ itens: Restricao[]; opcoes: OpcaoDeRestricao[] }>(
        `${BASE_MEU_ROBO}/restricoes`)).data,
    retry: false,
  })
}

export function useGravarRestricoes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: { itens: Restricao[] }) =>
      (await api.put<{ itens: Restricao[] }>(`${BASE_MEU_ROBO}/restricoes`, corpo)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE_RESTRICOES }),
  })
}

/**
 * Onde o robô avisa alerta crítico (spec 2026-09-30-robo-agente-pessoal §5.3): pares
 * grupo de Organização × ambiente. Só aceita grupo de que o dono participa e ambiente
 * que o robô acompanha hoje.
 */
export type Publicacao = { conversation_id: number; tenant_id: number }
export type GrupoDoDono = { conversation_id: number; titulo: string }
export type AmbienteDoRobo = { tenant_id: number; nome: string }
export const CHAVE_PUBLICACOES = ['meu-robo', 'publicacoes'] as const

export function usePublicacoesDoRobo() {
  return useQuery({
    queryKey: CHAVE_PUBLICACOES,
    queryFn: async () =>
      (await api.get<{ itens: Publicacao[]; grupos: GrupoDoDono[]; tenants: AmbienteDoRobo[] }>(
        `${BASE_MEU_ROBO}/publicacoes`)).data,
    retry: false,
  })
}

export function useGravarPublicacoes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: { itens: Publicacao[] }) =>
      (await api.put<{ itens: Publicacao[] }>(`${BASE_MEU_ROBO}/publicacoes`, corpo)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE_PUBLICACOES }),
  })
}

/**
 * A recusa de produção que vira pedido a quem pode analisar (spec 2026-09-30 §4, P3).
 * A lista traz só o que a pessoa pegaria agora — o servidor confere a permissão por
 * ambiente na hora — e os pedidos que ela já pegou.
 */
export type PedidoDeAnalise = {
  id: number; alvo: string; pergunta: string; ambiente: string; pedinte: string
  estado: 'aberto' | 'pego'; criado_em: string | null
}
export const CHAVE_PEDIDOS = ['robo', 'pedidos'] as const

export function usePedidosDeAnalise(opcoes: { refetchInterval?: number | false; enabled?: boolean } = {}) {
  return useQuery({
    enabled: opcoes.enabled ?? true,
    queryKey: CHAVE_PEDIDOS,
    queryFn: async () => (await api.get<{ itens: PedidoDeAnalise[] }>('/v1/robo/pedidos')).data.itens,
    refetchInterval: opcoes.refetchInterval ?? false,
    // o menu monta este hook em toda tela: sem staleTime, cada navegação refaz a leitura
    staleTime: 60_000,
    retry: false,
  })
}

export function usePegarPedido() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => (await api.post<{ pego: boolean }>(`/v1/robo/pedidos/${id}/pegar`)).data,
    // Só no sucesso: no 409/403 a lista recarregada tiraria o pedido da tela junto com
    // a mensagem que explica por que não deu para pegar.
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAVE_PEDIDOS }),
  })
}

export async function encaminharAnalise(cid: number, messageId: number) {
  return (await api.post<{ pedido_id: number; destinatarios: number }>(
    `${BASE_MEU_ROBO}/conversations/${cid}/encaminhar`, { message_id: messageId })).data
}

export async function definirEscopo(corpo: EscopoGravado): Promise<EscopoDoRobo> {
  return (await api.put<EscopoDoRobo>(`${BASE_MEU_ROBO}/escopo`, corpo)).data
}

/**
 * A linha da frota. Superconjunto de `Robo`: a rota por tenant devolve
 * `registry.CAMPOS`, que o `/me/robo` do chat nao precisava.
 *
 * `token_sha256` NAO esta aqui e nunca estara — `CAMPOS` nao o expoe, e
 * `tem_credencial` e' a EXISTENCIA dele, nao o valor.
 */
export type RoboFrota = {
  id: number
  public_id: string
  /**
   * `null` no robô cadastrado depois da spec 2026-09-24 (a frota é da
   * plataforma). Número só no robô legado, cujo firmware pode ainda falar pelo
   * caminho com tenant. NÃO autoriza nada: nenhuma rota de administração o usa.
   */
  tenant_id: number | null
  name: string
  location: string | null
  status: 'active' | 'suspended' | 'retired'
  token_expires_at: number | null
  firmware_version: string | null
  last_seen_at: string | null
  estado: ChaveEstado
  aviso_credencial: ChaveAviso
  created_at: string | null
  owner_user_id: number | null
  owner_named_at: string | null
  hardware_model: HardwareModel | null
  avatar: Avatar | null
  tem_credencial: boolean
  firmware: FirmwareVeredito
  /** Nome do tenant legado, quando o backend o resolve. */
  tenant_nome?: string | null
  /** MAC base da placa, gravado na primeira configuracao ou gravacao pela USB. */
  mac?: string | null
  /** A atribuicao em curso (spec 2026-09-27): quando, por quem e o que o dono respondeu. */
  assigned_at?: string | null
  assigned_by?: number | null
  receipt_state?: 'pendente' | 'recebido' | 'nao_recebido' | null
  receipt_at?: string | null
  /** So no detalhe: o nome de quem atribuiu. */
  atribuido_por_nome?: string | null
}

/** O que a emissao devolve UMA vez. O backend nao repete: quem nao copiou, rotaciona. */
export type CredencialEmitida = RoboFrota & { credential: string }

/**
 * Sem o tenant do seletor na chave (TASK-11262): a frota e o detalhe nao
 * dependem mais dele, e uma chave que o carregasse refaria a consulta a cada
 * troca de cliente no topo para devolver exatamente a mesma lista.
 */
export const CHAVE_FROTA = ['robo-frota'] as const
export const CHAVE_DETALHE = (publicId: string | undefined) =>
  ['robo-detalhe', publicId] as const

/**
 * A frota e' da plataforma (spec 2026-09-24-robo-sem-cliente, D1): nenhuma
 * rota de administracao leva tenant no caminho. O backend decide pela
 * permissao GLOBAL (`pode_administrar_robo`).
 */
const base = () => '/v1/devices/robo'
const um = (publicId: string) => `${base()}/${encodeURIComponent(publicId)}`

/** 401/403 repetido so enche log — a permissao nao muda entre os retries. */
const naoInsistirEmNegado = (contagem: number, erro: unknown) =>
  contagem < 1 && !(isAxiosError(erro) && [401, 403].includes(erro.response?.status ?? 0))

/** A frota inteira, sob `robo:read` global. O seletor do topo nao entra. */
export function useFrota() {
  return useQuery({
    queryKey: CHAVE_FROTA,
    queryFn: async (): Promise<RoboFrota[]> =>
      (await api.get<{ items: RoboFrota[] }>(base())).data.items,
    // 30 s e' o intervalo de polling do firmware: revalidar mais rapido que isso
    // mostra o mesmo `last_seen_at` de novo e so gera requisicao.
    refetchInterval: 30_000,
    retry: naoInsistirEmNegado,
  })
}

/**
 * O changelog de firmware por modelo (`/v1/devices/robo/releases`) — o que
 * mudou em cada versão, pra marcar o que o robô ainda não tem. Muda só com
 * deploy (o manifesto é carregado uma vez por processo no backend), daí o
 * `staleTime` alto: não há por que revalidar no intervalo de 30s da frota.
 */
export function useReleasesDeFirmware() {
  return useQuery({
    queryKey: ['robo-releases'] as const,
    queryFn: async (): Promise<ModeloDeRelease[]> =>
      (await api.get<{ modelos: ModeloDeRelease[] }>(`${base()}/releases`)).data.modelos,
    retry: naoInsistirEmNegado,
    staleTime: 5 * 60_000,
  })
}

/** O detalhe pelo ID publico. 404 tanto para inexistente quanto para sem permissao. */
export function useRoboDetalhe(publicId: string | undefined) {
  return useQuery({
    queryKey: CHAVE_DETALHE(publicId),
    enabled: !!publicId,
    queryFn: async (): Promise<RoboFrota> =>
      (await api.get<RoboFrota>(um(publicId as string))).data,
    refetchInterval: 30_000,
    retry: (contagem, erro) =>
      naoInsistirEmNegado(contagem, erro)
      && !(isAxiosError(erro) && erro.response?.status === 404),
  })
}

/**
 * O corpo do cadastro. `mac` e `hardware_model` vem da placa no cabo quando
 * ela foi lida (`LerRoboConectado`); ausentes, sao "ainda nao sei".
 */
export type CorpoCadastro = {
  name: string
  location?: string | null
  hardware_model?: HardwareModel
  mac?: string
}

/**
 * 409 do cadastro: a placa (MAC) ja' e' de um robo vivo. O backend devolve o
 * `public_id` dele, e a tela leva ate' la' em vez de criar um segundo.
 */
export const PLACA_JA_CADASTRADA = 'placa_ja_cadastrada'

/**
 * `null` se o erro nao e' a placa repetida. Sendo, `publicId` e' o robo que
 * ja' a tem — ou `null` na corrida de dois cadastros simultaneos em que o
 * backend nao achou o vencedor (`robo_admin_routes.cadastrar`).
 */
export function placaRepetida(erro: unknown): { publicId: string | null } | null {
  if (!isAxiosError(erro) || erro.response?.status !== 409) return null
  const detalhe = (erro.response.data as { detail?: unknown } | undefined)?.detail
  if (!detalhe || typeof detalhe !== 'object') return null
  const d = detalhe as { codigo?: unknown; public_id?: unknown }
  if (d.codigo !== PLACA_JA_CADASTRADA) return null
  return { publicId: typeof d.public_id === 'string' ? d.public_id : null }
}

export async function cadastrarRobo(corpo: CorpoCadastro): Promise<CredencialEmitida> {
  return (await api.post<CredencialEmitida>(base(), corpo)).data
}

export async function alterarRobo(
  publicId: string,
  corpo: {
    name?: string; location?: string | null; status?: 'active' | 'suspended'
    hardware_model?: HardwareModel
  },
): Promise<RoboFrota> {
  return (await api.patch<RoboFrota>(um(publicId), corpo)).data
}

export async function rotacionarCredencial(publicId: string): Promise<CredencialEmitida> {
  return (await api.post<CredencialEmitida>(`${um(publicId)}/rotate-credential`)).data
}

/**
 * A configuração que a página grava no robô pela USB (TASK-11350). Emite uma
 * credencial NOVA — a anterior deixa de valer na hora — e o claro vem só aqui.
 * Sem react-query de propósito: nada disto pode ficar em cache.
 */
/** O que a página grava no robô pela USB — a mesma forma na rota da Frota e na do dono. */
export type Provisionamento = {
  token: string
  /** Só o console anterior à revisão R2. */
  tenant_id?: number
  device_id: string
  snapshot_path: string
  /** Revisão R2: o caminho antigo, para o firmware que recusa o CONFIG sem tenant. */
  legado?: { tenant_id: number; snapshot_path: string }
  /**
   * A rede Wi-Fi padrão, só quando pedida (spec 2026-09-29-robo-wifi-padrao, 4.5).
   * A senha segue o destino do token: vai para a porta e sai de escopo.
   */
  wifi_padrao?: { ssid: string; senha: string }
}

export async function provisionarRobo(
  publicId: string, opcoes?: { comWifiPadrao?: boolean },
): Promise<Provisionamento> {
  // Sem corpo quando não se pede a rede: a rota antiga e a nova recebem o mesmo pedido.
  const url = `${um(publicId)}/provisionamento`
  return (await (opcoes?.comWifiPadrao ? api.post(url, { com_wifi_padrao: true }) : api.post(url))).data
}

// ── A rede Wi-Fi padrão dos robôs (spec 2026-09-29-robo-wifi-padrao, 4.1) ──

/** A rede padrão como o console a mostra: nunca a senha, nem o tamanho dela. */
export type RedePadrao = {
  ssid: string
  versao: number
  com_senha: boolean
  definida_por: number | null
  /** Epoch em segundos. */
  definida_em: number
}

export const CHAVE_REDE_PADRAO = ['robo-wifi-padrao'] as const

export function useRedePadrao(habilitado: boolean) {
  return useQuery({
    queryKey: CHAVE_REDE_PADRAO,
    queryFn: async (): Promise<RedePadrao | null> =>
      (await api.get<{ rede: RedePadrao | null }>(`${base()}/wifi-padrao`)).data.rede,
    enabled: habilitado,
    retry: naoInsistirEmNegado,
  })
}

/** Sem react-query: a senha vai como argumento, no corpo, e sai de escopo. */
export async function definirRedePadrao(ssid: string, senha: string): Promise<RedePadrao> {
  return (await api.put<{ rede: RedePadrao }>(`${base()}/wifi-padrao`, { ssid, senha })).data.rede
}

export async function removerRedePadrao(): Promise<void> {
  await api.delete(`${base()}/wifi-padrao`)
}

/**
 * A mesma configuração, pedida pelo DONO. O MAC é o que a página leu da placa
 * pelo INFO: o backend recusa a placa de outro robô (409 `placa_de_outro_robo`)
 * ou outra placa quando o robô já tem uma (409 `placa_diverge`).
 */
export async function provisionarMeuRobo(mac: string): Promise<Provisionamento> {
  return (await api.post(`${BASE_MEU_ROBO}/provisionamento`, { mac })).data
}

export async function aposentarRobo(publicId: string): Promise<RoboFrota> {
  return (await api.post<RoboFrota>(`${um(publicId)}/retire`)).data
}

export async function atribuirDono(publicId: string, userId: number): Promise<RoboFrota> {
  return (await api.post<RoboFrota>(`${um(publicId)}/assign`, { user_id: userId })).data
}

export async function desatribuirDono(publicId: string): Promise<RoboFrota> {
  return (await api.delete<RoboFrota>(`${um(publicId)}/assign`)).data
}

/**
 * O que o DONO configurou pro robô mostrar — somente leitura. Quem administra
 * a frota precisa enxergar para dar suporte, mas quem configura é o dono; por
 * isso não há mutação irmã deste hook. So' vem o canal 'robo': o backend já
 * filtra as outras preferências do dono antes de responder.
 */
export function useNotificacoesDoDono(publicId: string | undefined) {
  return useQuery({
    queryKey: ['robo-notificacoes-do-dono', publicId] as const,
    enabled: !!publicId,
    queryFn: async (): Promise<Subscription[]> =>
      (await api.get<{ items: Subscription[] }>(
        `${um(publicId as string)}/notification-subscriptions`)).data.items,
    retry: naoInsistirEmNegado,
  })
}

/** O manifesto da versão corrente do catálogo para o MODELO deste robô. */
export type ManifestoFirmware = {
  versao: string
  perfil: string
  commit: string
  sha256: string
  tamanho: number
  offset_app: number
  /** A fatia `app0` da tabela desta versão: 0x140000 (`default`) ou 0x1E0000 (`min_spiffs`). */
  fatia_app: number
  sha256_particoes: string
  criado_em: string
}

/**
 * `null` quando o catálogo não tem o que oferecer, com o motivo: robô sem
 * modelo cadastrado (409) ou modelo sem versão publicada (404). Os dois são
 * estado normal, não erro a reportar.
 */
export function useFirmwareDoRobo(publicId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['robo-firmware', publicId],
    enabled: habilitado,
    queryFn: async (): Promise<{ manifesto: ManifestoFirmware | null; motivo: string | null }> => {
      try {
        const r = await api.get<{ manifesto: ManifestoFirmware }>(`${um(publicId)}/firmware`)
        return { manifesto: r.data.manifesto, motivo: null }
      } catch (erro) {
        const status = isAxiosError(erro) ? erro.response?.status : undefined
        if (status === 409) return { manifesto: null, motivo: 'Cadastre o modelo de hardware do robô para o console saber qual firmware gravar.' }
        if (status === 404) return { manifesto: null, motivo: 'Ainda não há firmware publicado para o modelo deste robô.' }
        throw erro
      }
    },
    staleTime: 60_000,
    retry: naoInsistirEmNegado,
  })
}

/** Os bytes do `app.bin` daquela versão. A conferência do `sha256` é do gravador. */
export async function baixarFirmware(publicId: string, versao: string): Promise<Uint8Array> {
  const r = await api.get<ArrayBuffer>(`${um(publicId)}/firmware/app.bin`, {
    params: { versao }, responseType: 'arraybuffer',
  })
  return new Uint8Array(r.data)
}

/** Os bytes do `partitions.bin` daquela versão. A conferência é do gravador. */
export async function baixarParticoes(publicId: string, versao: string): Promise<Uint8Array> {
  const r = await api.get<ArrayBuffer>(`${um(publicId)}/firmware/partitions.bin`, {
    params: { versao }, responseType: 'arraybuffer',
  })
  return new Uint8Array(r.data)
}

/** A trilha da gravação — registrada no sucesso E na falha. */
export async function registrarGravacao(
  publicId: string,
  corpo: { versao: string; perfil: string; mac: string | null; resultado: 'ok' | 'falha'; passo: string | null },
): Promise<RoboFrota> {
  return (await api.post<RoboFrota>(`${um(publicId)}/gravacoes`, corpo)).data
}

/** Estado de uma ordem de atualização pelo ar (spec 2026-09-23-robo-ota). */
export type StatusOta = 'pendente' | 'baixando' | 'aplicado' | 'falhou' | 'cancelado'

export type MotivoOta = 'perfil' | 'tamanho' | 'sha256' | 'rede' | 'escrita' | 'voltou' | 'expirou'

/** Qual porta do download fechou em `falhou: rede` (TASK-11981). Espelho de
 * `ota.PORTAS` (robo/ota.py). `http` negativo e' o HTTPClient sem resposta. */
export type DetalheRedeOta = {
  porta: 'conexao' | 'http' | 'prazo' | 'fechou' | 'parado'
  http: number
  bytes: number
  ms: number
}

export type OrdemOta = {
  id: number
  versao: string
  perfil: HardwareModel
  status: StatusOta
  motivo: MotivoOta | null
  /** So' em `falhou: rede`, e so' de firmware que ja' o manda. */
  detalhe?: DetalheRedeOta | null
  pedido_por: number | null
  /** Epoch em segundos, como `token_expires_at`. */
  criado_em: number
  atualizado_em: number
}

export const OTA_ATIVOS: StatusOta[] = ['pendente', 'baixando']

/** 5 s com ordem ativa; parado quando ela termina (ou nao ha). */
export function intervaloDaOta(ordem: OrdemOta | null | undefined): number | false {
  return ordem && OTA_ATIVOS.includes(ordem.status) ? 5_000 : false
}

/**
 * A ordem mais recente, ativa ou não. Enquanto está ativa, pergunta a cada 5 s:
 * é o mesmo passo com que o robô consulta o snapshot quando há oferta, então a
 * tela não fica atrás do robô.
 */
export const CHAVE_OTA = (publicId: string) => ['robo-ota', publicId] as const

/** `candidata`: a versão da ordem está acima da homologada do modelo — o botão
 * de um robô entrega o topo do catálogo, que pode ainda ser de bancada. */
export type OtaDoRobo = { ordem: OrdemOta | null; candidata: boolean }

export function useOtaDoRobo(publicId: string) {
  return useQuery({
    queryKey: CHAVE_OTA(publicId),
    queryFn: async (): Promise<OtaDoRobo> =>
      (await api.get<OtaDoRobo>(`${um(publicId)}/ota`)).data,
    refetchInterval: (q) => intervaloDaOta(q.state.data?.ordem),
    retry: naoInsistirEmNegado,
  })
}

export async function pedirOta(publicId: string): Promise<OrdemOta> {
  return (await api.post<{ ordem: OrdemOta }>(`${um(publicId)}/ota`)).data.ordem
}

/** O que a prévia de homologação devolve (`robo/homologacao.previa`). */
export type MotivoHomologacao = 'versao_desconhecida' | 'ja_homologada' | 'abaixo_da_homologada' | 'sem_prova'
export type MotivoPulado = 'ja_na_versao' | 'candidata' | 'desenvolvimento' | 'modelo_divergente'
  | 'modelo_nao_cadastrado' | 'ordem_ativa' | 'nao_ativo' | 'anterior'
export type PreviaHomologacao = {
  pode: boolean
  motivo: MotivoHomologacao | null
  /** A ordem `aplicado` que prova a versão em bancada. */
  prova: { id: number; public_id: string; atualizado_em: number } | null
  alvos: { public_id: string; nome: string | null; versao_atual: string | null }[]
  pulados: { public_id: string; nome: string | null; motivo: MotivoPulado }[]
}

const homologacao = (perfil: string, versao: string) =>
  `${base()}/releases/${encodeURIComponent(perfil)}/${encodeURIComponent(versao)}/homologacao`

export const CHAVE_HOMOLOGACAO = (perfil: string, versao: string) =>
  ['robo-homologacao', perfil, versao] as const

export function usePreviaHomologacao(perfil: string, versao: string) {
  return useQuery({
    queryKey: CHAVE_HOMOLOGACAO(perfil, versao),
    queryFn: async (): Promise<PreviaHomologacao> =>
      (await api.get<PreviaHomologacao>(homologacao(perfil, versao))).data,
    retry: naoInsistirEmNegado,
  })
}

/** Homologa a release e cria a ordem de cada robô do modelo que está atrás. */
export async function homologarRelease(perfil: string, versao: string): Promise<void> {
  await api.post(homologacao(perfil, versao))
}

export async function cancelarOta(publicId: string): Promise<OrdemOta> {
  return (await api.delete<{ ordem: OrdemOta }>(`${um(publicId)}/ota`)).data.ordem
}

/**
 * Wi-Fi do robô à distância (spec 2026-09-24-robo-wifi-pela-frota-e-pela-usb,
 * 4.2 e 4.3). O relato é o que o robô disse: só SSID, nunca senha. A senha vai
 * no corpo do POST e não volta de rota nenhuma.
 */
export type RedeSalva = { ssid: string; confirmada: boolean; em_uso: boolean }
/** `auth` é o nome do modo como o firmware o dá (`WifiScanPolicy.h`); `open` é rede aberta. */
export type RedeVista = { ssid: string; rssi: number; auth: string }

export type RelatoWifi = {
  redes: RedeSalva[]
  /** O último scan, de uma ordem `escanear`. `null` se nunca houve. */
  scan: RedeVista[] | null
  firmware_version: string
  /** Epoch em segundos. */
  relatado_em: number
  scan_em: number | null
  /** O que o firmware diz saber fazer (`["modo_rede"]`). Vazio no firmware antigo. */
  capacidades: string[]
}

export type AcaoWifi = 'salvar' | 'esquecer' | 'escanear'
export type StatusWifi = 'pendente' | 'aplicando' | 'aplicado' | 'falhou' | 'cancelado'
export type MotivoWifi = 'formato' | 'rede' | 'ultima' | 'expirou' | 'firmware'

export type OrdemWifi = {
  id: number
  public_id: string
  acao: AcaoWifi
  ssid: string | null
  status: StatusWifi
  motivo: MotivoWifi | null
  pedido_por: number | null
  /** `padrao`: a entrega automática da rede padrão (spec 2026-09-29-robo-wifi-padrao, 4.4). */
  origem?: 'pedido' | 'padrao'
  padrao_versao?: number | null
  criado_em: number
  atualizado_em: number
  /**
   * A reação do robô quando ELE aplicou a ordem ("REDE SALVA"). `null` até lá:
   * o aviso não sai no pedido. Só a consulta devolve.
   */
  aviso?: AvisoDoAjuste | null
}

/**
 * A rede padrão neste robô (spec 2026-09-29-robo-wifi-padrao, 4.2). Só a rota
 * da Frota a devolve; a do dono não tem o campo.
 */
export type EstadoDaRedePadrao = {
  estado: 'gravada' | 'a_caminho' | 'aguardando' | 'falhou' | 'dispensada' | 'nao_se_aplica'
  motivo: MotivoWifi | null
}

/** `relato` nulo: o firmware não relata as redes, e ordem nenhuma é aceita. */
export type WifiDoRobo = {
  relato: RelatoWifi | null
  ordem: OrdemWifi | null
  /** `null`: não há rede padrão definida. Ausente: rota do dono. */
  padrao?: EstadoDaRedePadrao | null
}

export const WIFI_ATIVOS: StatusWifi[] = ['pendente', 'aplicando']

export function intervaloDoWifi(dados: WifiDoRobo | null | undefined): number | false {
  const ordem = dados?.ordem
  return ordem && WIFI_ATIVOS.includes(ordem.status) ? 5_000 : false
}

export const CHAVE_WIFI = (publicId: string) => ['robo-wifi', publicId] as const

/**
 * Por onde o cartão de Wi-Fi fala: a Frota pela rota do robô, o dono pela dele
 * (spec 2026-09-27-robo-recebimento, 4.3). O cartão é um só; o endereço decide.
 */
export type EnderecoDoWifi = {
  chave: readonly unknown[]
  url: string
  recibo: (avisoId: string) => string
}

export const enderecoDoWifiDaFrota = (publicId: string): EnderecoDoWifi => ({
  chave: CHAVE_WIFI(publicId),
  url: `${um(publicId)}/wifi`,
  recibo: (avisoId) => reciboDaFrota(publicId, avisoId),
})

export const enderecoDoWifiDoDono = (): EnderecoDoWifi => ({
  chave: ['me', 'robo-wifi'] as const,
  url: `${BASE_MEU_ROBO}/wifi`,
  recibo: (avisoId) => reciboDoDono(avisoId),
})

/** Mesmo passo do OTA: 5 s enquanto há ordem ativa, parado quando termina. */
export function useWifiDoRobo(endereco: EnderecoDoWifi) {
  return useQuery({
    queryKey: endereco.chave,
    queryFn: async (): Promise<WifiDoRobo> =>
      (await api.get<WifiDoRobo>(endereco.url)).data,
    refetchInterval: (q) => intervaloDoWifi(q.state.data),
    retry: naoInsistirEmNegado,
  })
}

/**
 * Sem react-query de propósito: a senha passa por aqui como argumento, vai no
 * corpo e sai de escopo. A resposta (a ordem) não a carrega.
 */
export async function pedirWifi(
  endereco: EnderecoDoWifi,
  pedido: { acao: 'escanear' } | { acao: 'esquecer'; ssid: string }
    | { acao: 'salvar'; ssid: string; senha: string },
): Promise<OrdemWifi> {
  return (await api.post<{ ordem: OrdemWifi }>(endereco.url, pedido)).data.ordem
}

export async function cancelarWifi(endereco: EnderecoDoWifi): Promise<OrdemWifi> {
  return (await api.delete<{ ordem: OrdemWifi }>(endereco.url)).data.ordem
}

// ── O robô reage na hora a cada ajuste (spec 2026-09-24-robo-painel-de-ajustes, 4.7) ──

/**
 * O que a rota que salvou conseguiu fazer com o aviso ao robô. Publicar é
 * best-effort: o ajuste vale em todos os casos.
 * - `publicado`: está na vaga do robô; o recibo diz se ele mostrou;
 * - `ocupado`: ele mostrava outra coisa; uma segunda tentativa vai em 4 s, e o
 *   recibo diz o desfecho;
 * - `offline`: não está falando com o Console agora;
 * - `sem_aviso1`: o firmware dele não mostra avisos;
 * - `indisponivel`: o canal (Redis) falhou.
 */
export type EstadoDoAviso = 'publicado' | 'ocupado' | 'offline' | 'sem_aviso1' | 'indisponivel'
export type AvisoDoAjuste = { id: string | null; estado: EstadoDoAviso }
/** Lido do ack que o robô grava — nunca suposto. */
export type ReciboDoAviso = 'pendente' | 'reagiu' | 'expirou' | 'ocupado' | 'indisponivel'

/** A tela consulta a cada 1 s, por até 25 s: 4 s da segunda tentativa + 20 s de validade. */
export const RECIBO_PASSO_MS = 1_000
export const RECIBO_PRAZO_MS = 25_000

export const reciboDoDono = (id: string) => `${BASE_MEU_ROBO}/aviso/${encodeURIComponent(id)}`
export const reciboDaFrota = (publicId: string, id: string) =>
  `${um(publicId)}/aviso/${encodeURIComponent(id)}`

/** `pendente` é o único estado que ainda muda. */
export function reciboFechou(recibo: ReciboDoAviso | undefined): boolean {
  return recibo !== undefined && recibo !== 'pendente'
}

/**
 * Consulta o recibo de UM aviso. Para sozinha quando fecha ou quando o prazo
 * passa — a tela nunca fica perguntando para sempre.
 */
export function useReciboDoAviso(caminho: string | null, desde: number) {
  return useQuery({
    queryKey: ['robo-aviso', caminho],
    enabled: caminho !== null,
    queryFn: async (): Promise<ReciboDoAviso> =>
      (await api.get<{ estado: ReciboDoAviso }>(caminho as string)).data.estado,
    refetchInterval: (q) =>
      reciboFechou(q.state.data) || Date.now() - desde >= RECIBO_PRAZO_MS ? false : RECIBO_PASSO_MS,
    retry: false,
    staleTime: 0,
  })
}

// ── "Começar de novo" (spec 2026-09-28-robo-recepcao-conduzida, 3.6) ──────────

/**
 * Manda o robô voltar ao estado de fábrica e reconduzir a configuração desde a
 * chegada. Devolve o número da ordem, que é o que o robô relata de volta.
 *
 * Sem react-query: é ação de um clique, e a ordem SUBSTITUI a anterior — não há
 * lista para invalidar. Quem mostra o efeito é o robô, na tela dele.
 */
export async function pedirRecomeco(
  publicId: string, { apagarConversa = false }: { apagarConversa?: boolean } = {},
): Promise<number> {
  return (await api.post<{ ordem: number }>(`${um(publicId)}/recomeco`,
    { apagar_conversa: apagarConversa })).data.ordem
}

// ── Ajustes do robô (spec 2026-09-24-robo-painel-de-ajustes, 6.2) ─────────────

export type SeveridadeMinima = 'warning' | 'average' | 'critical'
export type CanalDoAjuste = 'usb' | 'rede'

/** O estado desejado, como o servidor o guarda. `null` = o padrão (do firmware ou do servidor). */
export type AjustesDesejados = {
  versao: number
  brilho: number | null
  /** A tela do rosto: TFT no Robô II, OLED do rosto no Robô I. */
  rotacao: number | null
  /** A tela de informações, o OLED de resumo (TASK-24847). */
  rotacao_info: number | null
  reinicio: number
  intervalo_s: number | null
  severidade_min: SeveridadeMinima | null
}

/** O `X-Robo-Diag` da última consulta (`robo/diagnostico.py`); cada campo some quando veio fora da faixa. */
export type DiagDaRede = {
  up?: number
  heap?: number
  pilha?: number
  rssi?: number
  reset?: string
  idade_s: number
}

export type AjustesDoRobo = {
  desejado: AjustesDesejados
  /** A versão que o robô diz ter aplicado. `null` = não informou. */
  informado: number | null
  recusado: string[]
  diag: DiagDaRede | null
  /** O robô sabe ajustar pela rede E falou há menos de 120 s (spec 3.1). */
  rede: boolean
  /**
   * `null`: não falou pela rota com credencial própria nos últimos 15 min;
   * `false`: falou, mas o firmware não manda `X-Robo-Ajustes`.
   */
  capaz: boolean | null
  abaixo_do_limiar: number | null
  /**
   * A última recusa da credencial própria deste robô, nas últimas 24 h
   * (`robo/recusa.py`). `em` é epoch em segundos. Ausente no backend anterior.
   */
  recusa?: RecusaDoRobo | null
}

export type MotivoDaRecusa = 'credencial' | 'revogada' | 'sem_escopo'
export type RecusaDoRobo = { em: number; motivo: MotivoDaRecusa }

/** Só os campos ENVIADOS mudam; `null` volta ao padrão. */
export type MudancaDeAjustes = Partial<{
  brilho: number | null
  rotacao: number | null
  rotacao_info: number | null
  intervalo_s: number | null
  severidade_min: SeveridadeMinima | null
}>

export type RespostaDeAjuste = {
  versao: number
  desejado: AjustesDesejados
  aviso?: AvisoDoAjuste | null
}

export const CHAVE_AJUSTES = (publicId: string) => ['robo-ajustes', publicId] as const

/** 10 s: o retrato muda a cada consulta do robô (30 s), e a tela mostra "pendente há N s". */
export function useAjustesDoRobo(publicId: string | undefined) {
  return useQuery({
    queryKey: CHAVE_AJUSTES(publicId ?? ''),
    enabled: !!publicId,
    queryFn: async (): Promise<AjustesDoRobo> =>
      (await api.get<AjustesDoRobo>(`${um(publicId as string)}/ajustes`)).data,
    refetchInterval: 10_000,
    retry: naoInsistirEmNegado,
  })
}

/** Devolve a nova `versao`: é com ela que a tela manda o `AJUSTE` pela USB (spec 7). */
export async function alterarAjustes(
  publicId: string, mudancas: MudancaDeAjustes, canal: CanalDoAjuste,
): Promise<RespostaDeAjuste> {
  return (await api.put<RespostaDeAjuste>(`${um(publicId)}/ajustes`, { ...mudancas, canal })).data
}

/** Pela rede anda o contador `reinicio`; pela USB só registra (quem reinicia é o `REINICIAR`). */
export async function reiniciarRobo(publicId: string, canal: CanalDoAjuste): Promise<RespostaDeAjuste> {
  return (await api.post<RespostaDeAjuste>(`${um(publicId)}/ajustes/reiniciar`, { canal })).data
}

/** Tela e Operação voltam ao padrão. Não toca Wi-Fi, credencial nem dono. */
export async function restaurarAjustes(publicId: string, canal: CanalDoAjuste): Promise<RespostaDeAjuste> {
  return (await api.post<RespostaDeAjuste>(`${um(publicId)}/ajustes/padrao`, { canal })).data
}

/**
 * Grava no cadastro o MAC da placa lida pela USB, quando o cadastro não tem um
 * (spec 3.2). 409 `placa_de_outro_robo` ou `placa_diverge`: nada foi escrito.
 */
export async function vincularPlacaAoCadastro(publicId: string, mac: string): Promise<RoboFrota> {
  return (await api.post<RoboFrota>(`${um(publicId)}/placa`, { mac })).data
}

/** O código da recusa (`detail`) de uma resposta 4xx, quando é texto. */
export function codigoDaRecusa(erro: unknown): string | null {
  if (!isAxiosError(erro)) return null
  const d = (erro.response?.data as { detail?: unknown } | undefined)?.detail
  return typeof d === 'string' ? d : null
}

/**
 * Pergunta ao robô na conversa dele (spec 2026-10-02). A rota grava a pergunta
 * antes de responder e publica as duas mensagens no tempo real; o `onSettled`
 * só garante a tela de quem perguntou se o socket estiver fora.
 */
export function useRoboAsk(cid: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: { text: string; attachments: AskAttachmentRef[] }) =>
      (await api.post<{ interaction?: { status: string } | null }>(
        `${BASE_MEU_ROBO}/conversations/${cid}/ask`, corpo)).data,
    onError: (err: unknown) => {
      const resp = isAxiosError(err) ? err.response : undefined
      if (resp?.status === 403 || resp?.status === 404) {
        // Acesso revogado ou robô trocado: o portão confere de novo e troca a tela.
        void qc.invalidateQueries({ queryKey: ['robo-dm-portao'] })
        return
      }
      // 400 é o anexo recusado antes de gravar qualquer coisa (`_validar_anexos`).
      const anexo = resp?.status === 400
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: anexo
          ? (resp?.data?.detail === 'too_many_attachments'
              ? 'Anexos demais nesta mensagem.' : 'O anexo foi recusado. Nada foi enviado.')
          : 'Não foi possível enviar a mensagem.',
      })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['robo-status'] })
      void qc.invalidateQueries({ queryKey: ['conversation-messages', cid] })
    },
  })
}
